import { ApiClient, TokenManager } from '@/lib/api-client';
import { 
  AuthResponse, 
  LoginRequest, 
  RegisterRequest,
  User 
} from '@/types/api';

export class AuthService {
  /**
   * Login with email and password
   */
  static async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await ApiClient.post<AuthResponse>('/auth/login', data);
    
    if (response.success && response.data) {
      // Store tokens
      TokenManager.setTokens(response.data.tokens);
      return response.data;
    }
    
    throw new Error(response.message || 'Login failed');
  }

  /**
   * Register new user
   */
  static async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await ApiClient.post<AuthResponse>('/auth/register', data);
    
    if (response.success && response.data) {
      // Store tokens
      TokenManager.setTokens(response.data.tokens);
      return response.data;
    }
    
    throw new Error(response.message || 'Registration failed');
  }

  /**
   * Refresh access token
   */
  static async refreshToken(): Promise<AuthResponse> {
    const refreshToken = TokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await ApiClient.post<AuthResponse>('/auth/refresh', {
      refreshToken,
    });
    
    if (response.success && response.data) {
      // Update stored tokens
      TokenManager.setTokens(response.data.tokens);
      return response.data;
    }
    
    throw new Error(response.message || 'Token refresh failed');
  }

  /**
   * Get current user profile
   */
  static async getCurrentUser(): Promise<User> {
    const response = await ApiClient.get<User>('/auth/me');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get user profile');
  }

  /**
   * Logout (clear tokens)
   */
  static logout(): void {
    TokenManager.clearTokens();
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    const token = TokenManager.getAccessToken();
    return !!token;
  }

  /**
   * Google OAuth login — redirects to backend which initiates the Google flow.
   * After Google authenticates, backend redirects to /auth/callback with tokens.
   */
  static initiateGoogleLogin(): void {
    // Use relative path so that Vite proxy and ngrok work seamlessly
    window.location.href = `/api/auth/google`;
  }
}